import Papa from 'papaparse';
import GetQueuesBuilder from 'src/builders/GetQueuesBuilder';
import GetUserDetails from 'src/builders/GetUserDetails';
import GetUserGroupsBuilder from 'src/builders/GetUserGroupsBuilder';
import { DEFAULT_FILE_NAME } from 'src/constants/DisplayConstants';
import { API_ENDPOINTS } from 'src/constants/Urls';
import { STAGE } from 'src/constants/enums';
import { redirectToFailurePage } from 'src/utils/ErrorRedirectUtils';
import { FileUtils } from 'src/utils/FileUtils';
import { getCurrentStage } from '../StageConfigUtils';

export interface ExportCSVProps {
  fileName?: string;
  fileData?: any;
  csvExportEnabled?: boolean;
}

const MAPLE_ENABLED_DOMAINS: Record<STAGE, string[]> = {
  [STAGE.ALPHA]: [
    'BGC',
    'MC',
    'BXT_SE',
    'DT',
    'NJS',
    'WA',
    'BST',
    'IMH',
    'ATP',
    'DEPARTURES'
  ],
  [STAGE.BETA]: [
    'BGC',
    'MC',
    'BXT_SE',
    'DT',
    'NJS',
    'WA',
    'BST',
    'IMH',
    'ATP',
    'DEPARTURES'
  ],
  [STAGE.PROD]: [
    'BGC',
    'MC',
    'BXT_SE',
    'DT',
    'NJS',
    'WA',
    'BST',
    'IMH',
    'DEPARTURES'
  ]
};

export const isMapleMigratedDomain = (domainId: string) => {
  return MAPLE_ENABLED_DOMAINS[getCurrentStage()].includes(domainId);
};

export const getUserByEmployeeIDInQueue = async (
  employeeId,
  queueId,
  queueName,
  domainId,
  userList
) => {
  try {
    userList.initiateAsyncState();
    const getUserDetailsResponse = await GetUserDetails.getUserDetails(
      domainId,
      employeeId
    );
    const user = getUserDetailsResponse.user;
    const getQueuesResponse = await GetQueuesBuilder.getQueues(
      domainId,
      undefined,
      queueName,
      user.userId
    );
    const userMatchingQueues = getQueuesResponse.queues.filter(
      (queue, _index, _array) => {
        return queue.queueId == queueId;
      }
    );
    if (userMatchingQueues.length > 0) {
      userList.setAsyncSuccess([user]);
    } else {
      userList.setAsyncSuccess([]);
    }
  } catch (error) {
    userList.setAsyncFailure(error);
  }
};

export const getUserByEmployeeIDInUserGroup = async (
  employeeId,
  userGroupId,
  domainId,
  userList
) => {
  try {
    userList.initiateAsyncState();
    const getUserDetailsResponse = await GetUserDetails.getUserDetails(
      domainId,
      employeeId
    );
    const user = getUserDetailsResponse.user;
    const getGroupsResponse = await GetUserGroupsBuilder.getUserGroups(
      domainId,
      user.userId
    );
    const userMatchingGroups = getGroupsResponse.userGroups.filter(
      (group, _index, _array) => {
        return group.userGroupId == userGroupId;
      }
    );
    if (userMatchingGroups.length > 0) {
      userList.setAsyncSuccess([user]);
    } else {
      userList.setAsyncSuccess([]);
    }
  } catch (error) {
    userList.setAsyncFailure(error);
  }
};

export const getUsersForEmployeeIDInDomain = async (
  employeeId,
  domainId,
  userList
) => {
  try {
    userList.initiateAsyncState();
    const getUserDetailsResponse = await GetUserDetails.getUserDetails(
      domainId,
      employeeId
    );
    userList.setAsyncSuccess([getUserDetailsResponse.user]);
  } catch (error) {
    redirectToFailurePage(error, {
      source: 'CommonUtils - getUsersForEmployeeIDInDomain',
      api: API_ENDPOINTS.GET_USER_DETAILS
    });
    userList.setAsyncFailure(error);
  }
};

const exportDataInCSV = ({ fileName, fileData }) => {
  const data = Papa.unparse(fileData, { header: true });
  fileName = fileName ? fileName : DEFAULT_FILE_NAME;
  const fileType = 'text/csv';
  FileUtils.downloadFile({ data, fileName, fileType });
};

export const hasAccessPermission = (
  employeeId: string,
  allowlistedUsers: Set<string>
): boolean => {
  return allowlistedUsers.has(employeeId);
};

export const CommonUtils = { getUsersForEmployeeIDInDomain, exportDataInCSV };
